/**
 * IntegracionesTab — Integraciones externas del Centro de Ayuda.
 * - Grupo "CRM Inmobiliario" → "Consultar Cliente" (URL + API Key).
 * - Grupo "Integración Telefónica y WhatsApp" (Aircall) → 3 endpoints públicos con
 *   un switch on/off cada uno, una API Key única (regenerable) y guía de integración.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '../../../components/ui/dialog';
import { Loader2, Save, Building2, Lock, Eye, EyeOff, Phone, Copy, RefreshCw, BookOpen, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAbortableLoad, isCanceled } from '../../../hooks/useAbortableLoad';

export default function IntegracionesTab({ api, isAdmin }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // CRM
  const [activo, setActivo] = useState(false);
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiKeyMasked, setApiKeyMasked] = useState('');
  const [apiKeySet, setApiKeySet] = useState(false);
  const [showKey, setShowKey] = useState(false);
  // Omnicanal
  const [oc, setOc] = useState({
    consulta: false, crear: false, actualizar: false,
    apiKeyMasked: '', apiKeySet: false,
    urlConsulta: '', urlCrear: '', urlActualizar: '',
  });
  const [ocPlainKey, setOcPlainKey] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [showOcKey, setShowOcKey] = useState(false);
  const [revealingOc, setRevealingOc] = useState(false);

  const aplicar = (data) => {
    setActivo(!!data.crm_consultar_cliente_activo);
    setUrl(data.crm_consultar_cliente_url || '');
    setApiKeyMasked(data.crm_consultar_cliente_api_key_masked || '');
    setApiKeySet(!!data.crm_consultar_cliente_api_key_set);
    setApiKey('');
    setOc({
      consulta: !!data.omnicanal_consulta_cliente_activo,
      crear: !!data.omnicanal_crear_ticket_activo,
      actualizar: !!data.omnicanal_actualizar_ticket_activo,
      apiKeyMasked: data.omnicanal_api_key_masked || '',
      apiKeySet: !!data.omnicanal_api_key_set,
      urlConsulta: data.omnicanal_url_consulta || '',
      urlCrear: data.omnicanal_url_crear || '',
      urlActualizar: data.omnicanal_url_actualizar || '',
    });
  };

  const cargar = async (signal) => {
    setLoading(true);
    try {
      const { data } = await api.get('/tickets/admin/config-integraciones', { signal });
      aplicar(data);
    } catch (e) {
      if (isCanceled(e)) return;
      toast.error('No se pudo cargar la configuración de integraciones');
    } finally {
      setLoading(false);
    }
  };

  useAbortableLoad((signal) => cargar(signal), []);

  const persistir = async (payload, okMsg) => {
    setSaving(true);
    try {
      const { data } = await api.put('/tickets/admin/config-integraciones', payload);
      aplicar(data);
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar. ¿Ejecutaste las migraciones 030/053?');
    } finally {
      setSaving(false);
    }
  };

  const onToggleActivo = async (val) => {
    setActivo(val);
    await persistir({ crm_consultar_cliente_activo: val }, val ? 'Consulta de cliente activada' : 'Consulta de cliente desactivada');
  };

  const onGuardarConsultarCliente = async () => {
    if (!url.trim()) { toast.error('Ingresa la URL del servicio'); return; }
    const payload = { crm_consultar_cliente_activo: true, crm_consultar_cliente_url: url.trim() };
    if (apiKey.trim()) payload.crm_consultar_cliente_api_key = apiKey.trim();
    await persistir(payload, 'Configuración guardada');
  };

  // --- Omnicanal handlers ---
  const onToggleOc = async (campo, val) => {
    setOc((s) => ({ ...s, [campo]: val }));
    const map = {
      consulta: 'omnicanal_consulta_cliente_activo',
      crear: 'omnicanal_crear_ticket_activo',
      actualizar: 'omnicanal_actualizar_ticket_activo',
    };
    await persistir({ [map[campo]]: val });
  };

  const onRegenerarApiKey = async () => {
    if (!window.confirm('¿Regenerar la API Key? La clave anterior dejará de funcionar de inmediato.')) return;
    setRegenerating(true);
    try {
      const { data } = await api.post('/tickets/admin/config-integraciones/omnicanal/regenerar-api-key');
      aplicar(data);
      setOcPlainKey(data.omnicanal_api_key_plain || '');
      setShowOcKey(true);
      toast.success('Nueva API Key generada. Cópiala ahora: no se volverá a mostrar completa.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo regenerar la API Key');
    } finally {
      setRegenerating(false);
    }
  };

  const onToggleVerApiKey = async () => {
    // Ocultar si ya está visible
    if (showOcKey) { setShowOcKey(false); return; }
    // Si ya tenemos la clave en memoria (ej. tras regenerar), solo mostrarla
    if (ocPlainKey) { setShowOcKey(true); return; }
    setRevealingOc(true);
    try {
      const { data } = await api.get('/tickets/admin/config-integraciones/omnicanal/revelar-api-key');
      setOcPlainKey(data.omnicanal_api_key_plain || '');
      setShowOcKey(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo revelar la API Key');
    } finally {
      setRevealingOc(false);
    }
  };

  const copiar = (texto, label) => {
    if (!texto) return;
    navigator.clipboard.writeText(texto);
    toast.success(`${label} copiado`);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="integraciones-loading">
        <Loader2 className="w-7 h-7 animate-spin text-slate-400" />
      </div>
    );
  }

  const disabled = !activo || saving || !isAdmin;
  const ocDisabled = saving || regenerating || !isAdmin;

  return (
    <div className="space-y-8" data-testid="integraciones-tab">
      {/* GRUPO: CRM Inmobiliario */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="w-5 h-5 text-slate-700" />
          <h3 className="text-lg font-semibold text-slate-800">CRM Inmobiliario</h3>
        </div>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">Consultar Cliente</CardTitle>
                <CardDescription>
                  Enriquece automáticamente los datos del cliente (correo y presencial) desde el CRM.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Label htmlFor="crm-switch" className="text-sm text-slate-600 cursor-pointer">
                  Activar consulta cliente
                </Label>
                <Switch
                  id="crm-switch"
                  checked={activo}
                  onCheckedChange={onToggleActivo}
                  disabled={saving || !isAdmin}
                  data-testid="crm-consultar-cliente-switch"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">URL</Label>
              <Input
                type="url"
                placeholder="https://api.midominio.com/clientes"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={disabled}
                data-testid="crm-url-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> API Key
              </Label>
              <div className="relative">
                <Input
                  type={showKey ? 'text' : 'password'}
                  placeholder={apiKeySet ? `Configurada (${apiKeyMasked}) — deja vacío para conservar` : 'Ingresa la API Key'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  disabled={disabled}
                  className="pr-10"
                  autoComplete="off"
                  data-testid="crm-apikey-input"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  data-testid="crm-apikey-toggle"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <Button onClick={onGuardarConsultarCliente} disabled={disabled} data-testid="crm-consultar-cliente-save">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* GRUPO: Integración Telefónica y WhatsApp (Aircall) */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Phone className="w-5 h-5 text-slate-700" />
          <h3 className="text-lg font-semibold text-slate-800">Integración Telefónica y WhatsApp</h3>
        </div>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Endpoints públicos (Aircall)</CardTitle>
            <CardDescription>
              Cada llamada/conversación genera un ticket. Autentícate con el header <code className="text-xs bg-slate-100 px-1 rounded">X-API-Key</code>.
              Activa cada endpoint de forma independiente.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* API Key única */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> API Key (única para la integración)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={(showOcKey && ocPlainKey) ? ocPlainKey : (oc.apiKeySet ? `Configurada (${oc.apiKeyMasked})` : 'No generada')}
                  className="font-mono text-sm"
                  data-testid="omnicanal-apikey-display"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={onToggleVerApiKey}
                  disabled={revealingOc || (!oc.apiKeySet && !ocPlainKey)}
                  title={showOcKey ? 'Ocultar API Key' : 'Ver API Key'}
                  data-testid="omnicanal-apikey-toggle"
                >
                  {revealingOc ? <Loader2 className="w-4 h-4 animate-spin" /> : (showOcKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />)}
                </Button>
                {(showOcKey && ocPlainKey) && (
                  <Button variant="outline" size="icon" onClick={() => copiar(ocPlainKey, 'API Key')} data-testid="omnicanal-apikey-copy">
                    <Copy className="w-4 h-4" />
                  </Button>
                )}
                <Button variant="outline" onClick={onRegenerarApiKey} disabled={ocDisabled} data-testid="omnicanal-apikey-regenerate">
                  {regenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Regenerar
                </Button>
              </div>
              {(showOcKey && ocPlainKey) && (
                <p className="text-xs text-amber-600">Clave sensible: no la compartas. Úsala en el header <code className="bg-slate-100 px-1 rounded">X-API-Key</code> de la integración.</p>
              )}
            </div>

            {/* Endpoint 1: Consulta */}
            <EndpointRow
              titulo="Consulta de Cliente"
              descripcion="Busca un cliente por documento (solo lectura)."
              metodo="GET"
              urlValue={oc.urlConsulta}
              checked={oc.consulta}
              onToggle={(v) => onToggleOc('consulta', v)}
              disabled={ocDisabled}
              copiar={copiar}
              testid="consulta"
            />
            {/* Endpoint 2: Crear */}
            <EndpointRow
              titulo="Crear Ticket"
              descripcion="Crea el ticket al identificar al cliente."
              metodo="POST"
              urlValue={oc.urlCrear}
              checked={oc.crear}
              onToggle={(v) => onToggleOc('crear', v)}
              disabled={ocDisabled}
              copiar={copiar}
              testid="crear"
            />
            {/* Endpoint 3: Actualizar */}
            <EndpointRow
              titulo="Actualizar Ticket"
              descripcion="Completa/cierra el ticket (duración, grabación, transcripción, clasificación)."
              metodo="PATCH"
              urlValue={oc.urlActualizar}
              checked={oc.actualizar}
              onToggle={(v) => onToggleOc('actualizar', v)}
              disabled={ocDisabled}
              copiar={copiar}
              testid="actualizar"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EndpointRow({ titulo, descripcion, metodo, urlValue, checked, onToggle, disabled, copiar, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`omnicanal-endpoint-${testid}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono font-semibold px-1.5 py-0.5 rounded bg-slate-800 text-white">{metodo}</span>
            <span className="text-sm font-medium text-slate-800">{titulo}</span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{descripcion}</p>
        </div>
        <Switch
          checked={checked}
          onCheckedChange={onToggle}
          disabled={disabled}
          data-testid={`omnicanal-switch-${testid}`}
        />
      </div>
      <div className="flex items-center gap-2">
        <Input readOnly value={urlValue || '—'} className="font-mono text-xs" data-testid={`omnicanal-url-${testid}`} />
        <Button variant="outline" size="icon" onClick={() => copiar(urlValue, 'URL')} disabled={!urlValue} data-testid={`omnicanal-url-copy-${testid}`}>
          <Copy className="w-4 h-4" />
        </Button>
      </div>
      <div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="text-slate-600 h-7 px-2" data-testid={`omnicanal-guia-btn-${testid}`}>
              <BookOpen className="w-4 h-4 mr-1.5" /> Ver guía de integración
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid={`omnicanal-guia-dialog-${testid}`}>
            <GuiaContenido endpoint={testid} url={urlValue} />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function descargarComoMd(nombre, contenido) {
  const blob = new Blob([contenido], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${nombre}.md`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const GUIA_NOMBRE_ARCHIVO = { consulta: 'consulta-cliente', crear: 'crear-ticket', actualizar: 'actualizar-ticket' };

function construirMd(endpoint, url) {
  const urlBase = (url || '').split('?')[0] || 'https://<host>/api/tickets/omnicanal/<empresa_id>/...';
  if (endpoint === 'consulta') {
    return `# Guía: Consulta de Cliente

Busca un cliente por documento. Solo lectura: no crea ni modifica nada.

## Endpoint
\`\`\`
GET ${urlBase}?num_documento=<num_documento>
\`\`\`

## Autenticación
\`\`\`
X-API-Key: <api_key>
\`\`\`
Autenticación por API Key en el header \`X-API-Key\` (no Bearer). Prefijo \`oc_\`.

## Parámetros (query string)
- \`num_documento\` — **obligatorio**. El tipo de documento se deduce: DNI (8 dígitos), CE (9–12), Pasaporte (otro).

## Ejemplo (cURL)
\`\`\`
curl -X GET "${urlBase}?num_documento=72738721" -H "X-API-Key: <api_key>"
\`\`\`

## Respuesta 200 (encontrado)
\`\`\`json
{
  "encontrado": true,
  "descripcion": null,
  "nombre": "Juan Pérez García",
  "tipo_documento": "DNI",
  "documento": "72738721",
  "email": "juan.perez@correo.com",
  "celular": "+51987654321",
  "estado": "Propietario",
  "semaforo": "verde",
  "clasificacion": "Promotor",
  "es_critico": false,
  "nps": 9,
  "csat": 5,
  "unidades": [
    {
      "codigo_proyecto": "PAA",
      "nombre_proyecto": "Parque Amauta",
      "codigo_unidad": "A-1203",
      "unidad": "Departamento 1203",
      "subdivision": "Torre A",
      "tipo_unidad": "departamento",
      "estado_comercial": "entregado",
      "fecha_entrega": "2025-11-30",
      "porcentaje_participacion": 50.0
    }
  ]
}
\`\`\`

## Errores
- \`401\` — Falta la API Key o es inválida.
- \`403\` — El endpoint "Consulta de Cliente" está desactivado.

## Notas
- **estado**: "Propietario" si tiene ≥1 unidad con \`estado_comercial = "entregado"\`; si no, "No Propietario".
`;
  }
  if (endpoint === 'crear') {
    return `# Guía: Crear Ticket

Crea el ticket al identificar al cliente. Vincula al cliente existente o lo crea con datos mínimos.

## Endpoint
\`\`\`
POST ${urlBase}
\`\`\`

## Autenticación
\`\`\`
X-API-Key: <api_key>
Content-Type: application/json
\`\`\`

## Cuerpo (JSON)
- \`num_documento\` — **obligatorio**.
- \`canal_atencion\` — **obligatorio**. "TELEFONO" o "WHATSAPP".
- \`asunto_ticket\` — **obligatorio**.
- \`descripcion_ticket\` — **obligatorio**.
- \`nombre_responsable\` — opcional. Si coincide con un usuario activo, el ticket nace ASIGNADO; si no, se asigna a un usuario de un área Frontline.
- \`id_interaccion\` — opcional. Id de la llamada/conversación en Aircall.
- \`codigo_punto_contacto\` — opcional.

## Ejemplo A — TELEFONO (cURL)
\`\`\`
curl -X POST "${urlBase}" \\
  -H "X-API-Key: <api_key>" -H "Content-Type: application/json" \\
  -d '{
    "num_documento": "72738721",
    "canal_atencion": "TELEFONO",
    "asunto_ticket": "Consulta de cronograma de pagos",
    "descripcion_ticket": "El cliente solicita el detalle de su próxima cuota.",
    "nombre_responsable": "María Torres",
    "id_interaccion": "AIRCALL-8891234",
    "codigo_punto_contacto": "SAC_TELEFONICA"
  }'
\`\`\`

## Ejemplo B — WHATSAPP (cURL)
\`\`\`
curl -X POST "${urlBase}" \\
  -H "X-API-Key: <api_key>" -H "Content-Type: application/json" \\
  -d '{
    "num_documento": "72738721",
    "canal_atencion": "WHATSAPP",
    "asunto_ticket": "Consulta por WhatsApp",
    "descripcion_ticket": "El cliente escribe para pedir su estado de cuenta.",
    "nombre_responsable": "María Torres",
    "id_interaccion": "AIRCALL-WA-556677",
    "codigo_punto_contacto": "SAC_WHATSAPP"
  }'
\`\`\`

## Respuesta 200
\`\`\`json
{ "id_ticket": "uuid", "id_cliente": "uuid", "cliente_creado": false }
\`\`\`

## Errores
- \`400\` — num_documento vacío o canal_atencion no existe/está inactivo.
- \`401\` / \`403\` — API Key inválida / endpoint desactivado.

## Notas
- Prioridad estándar; clasificación pendiente (se completa en Actualizar). Cliente creado si no existía.
`;
  }
  // actualizar
  return `# Guía: Actualizar Ticket

Completa un ticket ya creado con lo recaudado durante o después de la interacción, hasta cerrarlo.

## Endpoint
\`\`\`
PATCH ${urlBase.replace('{id_ticket}', '<id_ticket>')}
\`\`\`

## Autenticación
\`\`\`
X-API-Key: <api_key>
Content-Type: application/json
\`\`\`

## Cuerpo (JSON)
- \`estado_ticket\` — "" mantiene el estado; "TERMINADO" cierra el ticket.
- \`clasificacion_ticket\` — Clasificación N1. Si vacío, se deduce de \`transcripcion\` (IA) y se halla el N2.
- \`duracion_seg\` — duración de la interacción.
- \`enlace_grabacion\` — URL de la grabación. **Solo TELEFONO**.
- \`transcripcion\` — transcripción. **Solo TELEFONO**.
- \`primera_respuesta_seg\` — segundos hasta la primera respuesta. **Solo WHATSAPP**.
- \`resuelto_en_interaccion\` — true si se atendió en la interacción; false si requiere gestión posterior.
- \`mensaje\` — **opcional**. Texto que se registra en el timeline del ticket como si lo hubiera enviado el Responsable asignado. Si trae datos, se registra **antes** de pasar el ticket a "TERMINADO".
- \`num_telefono\` — **obligatorio**. Número telefónico real desde el que el cliente llamó/escribió efectivamente (puede diferir del registrado en su ficha).

## Ejemplo A — TELEFONO (cURL)
\`\`\`
curl -X PATCH "${urlBase.replace('{id_ticket}', '<id_ticket>')}" \\
  -H "X-API-Key: <api_key>" -H "Content-Type: application/json" \\
  -d '{
    "estado_ticket": "TERMINADO",
    "clasificacion_ticket": "",
    "duracion_seg": 185,
    "enlace_grabacion": "https://aircall.example/rec/8891234.mp3",
    "transcripcion": "Buenos días, llamo para consultar mi cronograma de pagos...",
    "resuelto_en_interaccion": true,
    "mensaje": "Se atendió la consulta del cliente y se envió el cronograma actualizado.",
    "num_telefono": "+51987654321"
  }'
\`\`\`

## Ejemplo B — WHATSAPP (cURL)
\`\`\`
curl -X PATCH "${urlBase.replace('{id_ticket}', '<id_ticket>')}" \\
  -H "X-API-Key: <api_key>" -H "Content-Type: application/json" \\
  -d '{
    "estado_ticket": "TERMINADO",
    "clasificacion_ticket": "Pagos",
    "duracion_seg": 420,
    "primera_respuesta_seg": 45,
    "resuelto_en_interaccion": true,
    "num_telefono": "+51987654321"
  }'
\`\`\`

## Respuesta 200
\`\`\`json
{ "estado_ticket": "TERMINADO" }
\`\`\`

## Errores
- \`404\` — El ticket no existe para esta empresa.
- \`422\` — Falta el campo obligatorio \`num_telefono\`.
- \`401\` / \`403\` — API Key inválida / endpoint desactivado.

## Notas
- Idempotente por \`id_interaccion\` (soporta varios PATCH, ej. la transcripción llega tras el cierre).
- \`num_telefono\` se muestra en la sección **Detalles** del ticket cuando el canal es TELEFONO.
- \`mensaje\` (si viene) queda en el timeline como mensaje SALIENTE del Responsable asignado; si el ticket aún no tiene responsable, se registra igualmente como AGENTE sin usuario.
- Al TERMINAR un ticket de canal WHATSAPP, la plataforma envía automáticamente la encuesta EPA por WhatsApp.
`;
}

function DescargarMdBtn({ endpoint, url }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <Button variant="outline" size="sm" data-testid={`omnicanal-guia-md-${endpoint}`}
        onClick={() => descargarComoMd(GUIA_NOMBRE_ARCHIVO[endpoint], construirMd(endpoint, url))}>
        <Download className="w-4 h-4 mr-2" /> Descargar .md
      </Button>
    </div>
  );
}

function GuiaContenido({ endpoint, url }) {
  const urlBase = (url || '').split('?')[0] || 'https://<host>/api/tickets/omnicanal/<empresa_id>/...';
  const Seccion = ({ titulo, children }) => (
    <div>
      <h3 className="font-semibold mb-1">{titulo}</h3>
      {children}
    </div>
  );
  const Pre = ({ children, dark }) => (
    <pre className={`rounded-lg p-3 text-xs overflow-x-auto ${dark ? 'bg-slate-900 text-slate-100' : 'bg-slate-50 border border-slate-200'}`}>
      <code>{children}</code>
    </pre>
  );
  const Auth = () => (
    <Seccion titulo="Autenticación">
      <Pre>{`X-API-Key: <api_key>
Content-Type: application/json`}</Pre>
      <p className="text-xs text-slate-500 mt-1">
        Autenticación por API Key en el header <span className="font-mono">X-API-Key</span> (no Bearer).
        Usa la API Key única de esta integración (prefijo <span className="font-mono">oc_</span>).
      </p>
    </Seccion>
  );

  if (endpoint === 'consulta') {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5" /> Guía: Consulta de Cliente</DialogTitle>
          <DialogDescription>Busca un cliente por documento. Solo lectura: no crea ni modifica nada.</DialogDescription>
        </DialogHeader>
        <DescargarMdBtn endpoint="consulta" url={url} />
        <div className="space-y-4 text-sm text-slate-700">
          <Seccion titulo="Endpoint"><Pre>{`GET ${urlBase}?num_documento=<num_documento>`}</Pre></Seccion>
          <Auth />
          <Seccion titulo="Parámetros (query string)">
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li><span className="font-mono">num_documento</span> — <b>obligatorio</b>. El tipo de documento se deduce automáticamente: DNI (8 dígitos), CE (9–12 dígitos), Pasaporte (otro).</li>
            </ul>
          </Seccion>
          <Seccion titulo="Ejemplo (cURL)">
            <Pre dark>{`curl -X GET "${urlBase}?num_documento=72738721" \\
  -H "X-API-Key: <api_key>"`}</Pre>
          </Seccion>
          <Seccion titulo="Respuesta (200) — cliente encontrado">
            <Pre>{`{
  "encontrado": true,
  "descripcion": null,               // texto si la ficha está incompleta
  "nombre": "Juan Pérez García",
  "tipo_documento": "DNI",           // "DNI" | "CE" | "Pasaporte"
  "documento": "72738721",
  "email": "juan.perez@correo.com",
  "celular": "+51987654321",
  "estado": "Propietario",           // "Propietario" | "No Propietario"
  "semaforo": "verde",               // verde | ambar | rojo | null
  "clasificacion": "Promotor",       // "Detractor" | "Pasivo" | "Promotor"
  "es_critico": false,
  "nps": 9,
  "csat": 5,
  "unidades": [
    {
      "codigo_proyecto": "PAA",
      "nombre_proyecto": "Parque Amauta",
      "codigo_unidad": "A-1203",
      "unidad": "Departamento 1203",
      "subdivision": "Torre A",       // Torre / etapa / null
      "tipo_unidad": "departamento",
      "estado_comercial": "entregado",
      "fecha_entrega": "2025-11-30",  // con valor solo si estado_comercial = "entregado"
      "porcentaje_participacion": 50.0
    }
  ]
}`}</Pre>
          </Seccion>
          <Seccion titulo="Respuesta (200) — cliente no encontrado">
            <Pre>{`{
  "encontrado": false,
  "descripcion": "Cliente no encontrado en la base de datos.",
  "tipo_documento": "DNI",
  "documento": "72738721",
  "es_critico": false,
  "unidades": []
}`}</Pre>
          </Seccion>
          <Seccion titulo="Errores">
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li><span className="font-mono">401</span> — Falta la API Key o es inválida.</li>
              <li><span className="font-mono">403</span> — El endpoint "Consulta de Cliente" está desactivado en la configuración.</li>
            </ul>
          </Seccion>
          <Seccion titulo="Notas">
            <p className="text-xs"><b>estado</b>: "Propietario" si el cliente tiene ≥1 unidad con <span className="font-mono">estado_comercial = "entregado"</span>; en caso contrario "No Propietario". <b>Semáforo/NPS/CSAT</b>: se calculan sobre la encuesta más reciente del cliente.</p>
          </Seccion>
        </div>
      </>
    );
  }

  if (endpoint === 'crear') {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5" /> Guía: Crear Ticket</DialogTitle>
          <DialogDescription>Crea el ticket al identificar al cliente. Vincula al cliente existente o lo crea con datos mínimos.</DialogDescription>
        </DialogHeader>
        <DescargarMdBtn endpoint="crear" url={url} />
        <div className="space-y-4 text-sm text-slate-700">
          <Seccion titulo="Endpoint"><Pre>{`POST ${urlBase}`}</Pre></Seccion>
          <Auth />
          <Seccion titulo="Cuerpo (JSON)">
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li><span className="font-mono">num_documento</span> — <b>obligatorio</b>. Documento del cliente (el tipo se deduce).</li>
              <li><span className="font-mono">canal_atencion</span> — <b>obligatorio</b>. Código del canal: <span className="font-mono">"TELEFONO"</span> o <span className="font-mono">"WHATSAPP"</span>.</li>
              <li><span className="font-mono">asunto_ticket</span> — <b>obligatorio</b>. Asunto del ticket.</li>
              <li><span className="font-mono">descripcion_ticket</span> — <b>obligatorio</b>. Descripción/motivo.</li>
              <li><span className="font-mono">nombre_responsable</span> — opcional. Si coincide con un usuario activo, el ticket nace <span className="font-mono">ASIGNADO</span>; si no, <span className="font-mono">NUEVO</span>.</li>
              <li><span className="font-mono">id_interaccion</span> — opcional. Id de la llamada/conversación en Aircall.</li>
              <li><span className="font-mono">codigo_punto_contacto</span> — opcional. Código del punto de contacto (CJM).</li>
            </ul>
          </Seccion>
          <Seccion titulo="Ejemplo A — TELEFONO (cURL)">
            <Pre dark>{`curl -X POST "${urlBase}" \\
  -H "X-API-Key: <api_key>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "num_documento": "72738721",
    "canal_atencion": "TELEFONO",
    "asunto_ticket": "Consulta de cronograma de pagos",
    "descripcion_ticket": "El cliente solicita el detalle de su próxima cuota.",
    "nombre_responsable": "María Torres",
    "id_interaccion": "AIRCALL-8891234",
    "codigo_punto_contacto": "SAC_TELEFONICA"
  }'`}</Pre>
          </Seccion>
          <Seccion titulo="Ejemplo B — WHATSAPP (cURL)">
            <Pre dark>{`curl -X POST "${urlBase}" \\
  -H "X-API-Key: <api_key>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "num_documento": "72738721",
    "canal_atencion": "WHATSAPP",
    "asunto_ticket": "Consulta por WhatsApp",
    "descripcion_ticket": "El cliente escribe para pedir su estado de cuenta.",
    "nombre_responsable": "María Torres",
    "id_interaccion": "AIRCALL-WA-556677",
    "codigo_punto_contacto": "SAC_WHATSAPP"
  }'`}</Pre>
          </Seccion>
          <Seccion titulo="Respuesta (200)">
            <Pre>{`{
  "id_ticket": "b7334e9b-25c7-4c06-a877-df18b559a44d",
  "id_cliente": "f2c8a6d9-9377-43b9-841e-712880b46b95",
  "cliente_creado": false            // true si el cliente no existía y se creó
}`}</Pre>
          </Seccion>
          <Seccion titulo="Errores">
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li><span className="font-mono">400</span> — <span className="font-mono">num_documento</span> vacío, o <span className="font-mono">canal_atencion</span> no existe / está inactivo.</li>
              <li><span className="font-mono">401</span> — Falta la API Key o es inválida.</li>
              <li><span className="font-mono">403</span> — El endpoint "Crear Ticket" está desactivado.</li>
            </ul>
          </Seccion>
          <Seccion titulo="Notas">
            <p className="text-xs">La prioridad se asigna como <b>estándar</b> (default de la empresa) y la <b>clasificación queda pendiente</b> de llenado (se completa en Actualizar Ticket). Los datos del cliente se completan desde su documento; si no existe, se crea con datos mínimos.</p>
          </Seccion>
        </div>
      </>
    );
  }

  // actualizar
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5" /> Guía: Actualizar Ticket</DialogTitle>
        <DialogDescription>Completa un ticket ya creado con lo recaudado durante o después de la interacción, hasta cerrarlo.</DialogDescription>
      </DialogHeader>
      <DescargarMdBtn endpoint="actualizar" url={url} />
      <div className="space-y-4 text-sm text-slate-700">
        <Seccion titulo="Endpoint"><Pre>{`PATCH ${urlBase.replace('{id_ticket}', '<id_ticket>')}`}</Pre></Seccion>
        <Auth />
        <Seccion titulo="Cuerpo (JSON)">
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li><span className="font-mono">estado_ticket</span> — cadena vacía <span className="font-mono">""</span> mantiene el estado actual; <span className="font-mono">"TERMINADO"</span> cierra el ticket.</li>
            <li><span className="font-mono">clasificacion_ticket</span> — Clasificación Nivel N1. Si viene vacío, se deduce desde <span className="font-mono">transcripcion</span> (IA) y además se halla el N2 correspondiente.</li>
            <li><span className="font-mono">duracion_seg</span> — duración de la interacción (segundos).</li>
            <li><span className="font-mono">enlace_grabacion</span> — URL de la grabación. <b>Solo canal TELEFONO</b>.</li>
            <li><span className="font-mono">transcripcion</span> — texto de la transcripción (suele llegar después del cierre). <b>Solo canal TELEFONO</b>.</li>
            <li><span className="font-mono">primera_respuesta_seg</span> — segundos hasta la primera respuesta. <b>Solo canal WHATSAPP</b>.</li>
            <li><span className="font-mono">resuelto_en_interaccion</span> — <span className="font-mono">true</span> si la solicitud se atendió durante la interacción; <span className="font-mono">false</span> si requiere gestión posterior.</li>
            <li><span className="font-mono">mensaje</span> — <b>opcional</b>. Texto que se registra en el timeline del ticket como si lo hubiera enviado el <b>Responsable asignado</b>. Si trae datos, se registra <b>antes</b> de pasar el ticket a <span className="font-mono">"TERMINADO"</span>.</li>
            <li><span className="font-mono">num_telefono</span> — <b>obligatorio</b>. Número telefónico real desde el que el cliente llamó/escribió efectivamente (puede diferir del registrado en su ficha). Se muestra en la sección <b>Detalles</b> del ticket cuando el canal es TELEFONO.</li>
          </ul>
        </Seccion>
        <Seccion titulo="Ejemplo A — TELEFONO (cURL)">
          <Pre dark>{`curl -X PATCH "${urlBase.replace('{id_ticket}', 'b7334e9b-25c7-4c06-a877-df18b559a44d')}" \\
  -H "X-API-Key: <api_key>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "estado_ticket": "TERMINADO",
    "clasificacion_ticket": "",
    "duracion_seg": 185,
    "enlace_grabacion": "https://aircall.example/rec/8891234.mp3",
    "transcripcion": "Buenos días, llamo para consultar mi cronograma de pagos...",
    "resuelto_en_interaccion": true,
    "mensaje": "Se atendió la consulta del cliente y se envió el cronograma actualizado.",
    "num_telefono": "+51987654321"
  }'`}</Pre>
        </Seccion>
        <Seccion titulo="Ejemplo B — WHATSAPP (cURL)">
          <Pre dark>{`curl -X PATCH "${urlBase.replace('{id_ticket}', 'b7334e9b-25c7-4c06-a877-df18b559a44d')}" \\
  -H "X-API-Key: <api_key>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "estado_ticket": "TERMINADO",
    "clasificacion_ticket": "Pagos",
    "duracion_seg": 420,
    "primera_respuesta_seg": 45,
    "resuelto_en_interaccion": true,
    "num_telefono": "+51987654321"
  }'`}</Pre>
          <p className="text-xs text-slate-500 mt-1">Al TERMINAR un ticket de canal WHATSAPP, la plataforma envía automáticamente la encuesta EPA por WhatsApp.</p>
        </Seccion>
        <Seccion titulo="Respuesta (200)">
          <Pre>{`{
  "estado_ticket": "TERMINADO"       // estado en que quedó el ticket
}`}</Pre>
        </Seccion>
        <Seccion titulo="Errores">
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li><span className="font-mono">404</span> — El ticket no existe para esta empresa.</li>
            <li><span className="font-mono">422</span> — Falta el campo obligatorio <span className="font-mono">num_telefono</span>.</li>
            <li><span className="font-mono">401</span> — Falta la API Key o es inválida.</li>
            <li><span className="font-mono">403</span> — El endpoint "Actualizar Ticket" está desactivado.</li>
          </ul>
        </Seccion>
        <Seccion titulo="Notas">
          <p className="text-xs">El endpoint es <b>idempotente por</b> <span className="font-mono">id_interaccion</span>: puedes llamarlo varias veces (por ejemplo, un primer PATCH al cerrar y un segundo con la transcripción cuando esté disponible). El campo <span className="font-mono">mensaje</span> (si viene) queda en el timeline como mensaje SALIENTE del Responsable asignado; si el ticket aún no tiene responsable, se registra igualmente como AGENTE sin usuario. La encuesta EPA (NPS/CSAT) se ingesta por separado con la API de <b>Configuración CX → Ingesta de Encuestas</b>.</p>
        </Seccion>
      </div>
    </>
  );
}
